import React, { useEffect, useState, useCallback } from "react";
import { CalendarDays, Clock, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { useLanguage } from "../i18n";
import { PageHero, Reveal } from "../components/Shared";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Calendar } from "../components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INITIAL = { name: "", email: "", phone: "", service: "", notes: "" };

const Appointment = () => {
  const [form, setForm] = useState(INITIAL);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [taken, setTaken] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const { lang, t, data } = useLanguage();
  const tr = t.rdvPage;
  const dateLocale = lang === "fr" ? fr : enUS;

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const fetchAvailability = useCallback(async (d) => {
    setLoadingSlots(true);
    setSelectedSlot("");
    try {
      const dateStr = format(d, "yyyy-MM-dd");
      const res = await axios.get(`${API}/appointments/availability`, { params: { date: dateStr } });
      setSlots(res.data.slots);
      setTaken(res.data.taken);
    } catch (err) {
      setSlots([]);
      setTaken([]);
      toast.error(tr.slotsError);
    } finally {
      setLoadingSlots(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (date) fetchAvailability(date);
  }, [date, fetchAvailability]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error(tr.requiredName);
      return;
    }
    if (!form.service) {
      toast.error(tr.requiredService);
      return;
    }
    if (!date || !selectedSlot) {
      toast.error(tr.requiredSlot);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        date: format(date, "yyyy-MM-dd"),
        time_slot: selectedSlot,
      };
      const res = await axios.post(`${API}/appointments`, payload);
      setConfirmed(res.data);
      toast.success(tr.successToast);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : tr.errorToast);
      if (err?.response?.status === 409 && date) fetchAvailability(date);
    } finally {
      setSubmitting(false);
    }
  };

  const disabledDays = (d) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today || d.getDay() === 0 || d.getDay() === 6;
  };

  if (confirmed) {
    return (
      <div data-testid="appointment-page">
        <PageHero
          title={tr.title}
          subtitle={tr.subtitle}
          image="https://images.unsplash.com/photo-1443527394413-4b820fd08dde?w=1920&q=80"
        />
        <section className="py-24 bg-white">
          <div className="max-w-xl mx-auto px-5 text-center" data-testid="appointment-confirmation">
            <div className="w-20 h-20 rounded-full bg-[#eef3fc] flex items-center justify-center mx-auto mb-7">
              <CheckCircle2 size={40} className="text-[#2547a8]" />
            </div>
            <h2 className="text-3xl font-semibold text-[#1b2653] mb-4">{tr.confirmedTitle}</h2>
            <p className="text-slate-600 font-light leading-relaxed mb-8">
              {tr.confirmedText1}{" "}
              <strong className="font-semibold text-[#1b2653]">{confirmed.service}</strong>{" "}
              {tr.confirmedText2}{" "}
              <strong className="font-semibold text-[#1b2653]">
                {format(new Date(confirmed.date + "T00:00:00"), "EEEE d MMMM yyyy", { locale: dateLocale })}
              </strong>{" "}
              {tr.confirmedText3} <strong className="font-semibold text-[#1b2653]">{confirmed.time_slot}</strong>{" "}
              {tr.confirmedText4}
            </p>
            <button
              onClick={() => {
                setConfirmed(null);
                setForm(INITIAL);
                setDate(null);
                setSelectedSlot("");
              }}
              className="inline-flex items-center justify-center rounded-full px-8 py-3.5 text-[13px] font-semibold uppercase tracking-[0.18em] bg-[#1b2653] text-white hover:bg-[#2f6fd8] transition-colors duration-300"
              data-testid="appointment-new-btn"
            >
              {tr.newRdv}
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="appointment-page">
      <PageHero
        title={tr.title}
        subtitle={tr.subtitle}
        image="https://images.unsplash.com/photo-1443527394413-4b820fd08dde?w=1920&q=80"
      />

      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <form onSubmit={handleSubmit} data-testid="appointment-form">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Colonne gauche : date + créneaux */}
              <Reveal>
                <div className="bg-[#f5f7fb] rounded-lg p-7 md:p-9">
                  <h3 className="flex items-center gap-2.5 text-lg font-semibold text-[#1b2653] mb-6">
                    <CalendarDays size={20} className="text-[#2547a8]" />
                    {tr.step1}
                  </h3>
                  <div className="bg-white rounded-lg border border-slate-200 p-3 inline-block">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={disabledDays}
                      locale={dateLocale}
                      data-testid="appointment-calendar"
                    />
                  </div>

                  <h3 className="flex items-center gap-2.5 text-lg font-semibold text-[#1b2653] mt-8 mb-5">
                    <Clock size={20} className="text-[#2547a8]" />
                    {tr.step2}
                  </h3>
                  {!date ? (
                    <p className="text-slate-500 text-sm font-light">{tr.selectDateFirst}</p>
                  ) : loadingSlots ? (
                    <p className="text-slate-500 text-sm font-light">{tr.loadingSlots}</p>
                  ) : (
                    <div className="grid grid-cols-4 gap-3" data-testid="appointment-slots">
                      {slots.map((slot) => {
                        const isTaken = taken.includes(slot);
                        const isSelected = selectedSlot === slot;
                        return (
                          <button
                            key={slot}
                            type="button"
                            disabled={isTaken}
                            onClick={() => setSelectedSlot(slot)}
                            data-testid={`slot-${slot.replace(":", "-")}`}
                            className={`py-2.5 rounded-md text-sm font-medium border transition-colors duration-300 ${
                              isTaken
                                ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed line-through"
                                : isSelected
                                ? "bg-[#1b2653] text-white border-[#1b2653]"
                                : "bg-white text-[#1b2653] border-slate-300 hover:border-[#2f6fd8] hover:text-[#2f6fd8]"
                            }`}
                          >
                            {slot}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Reveal>

              {/* Colonne droite : coordonnées */}
              <Reveal delay={100}>
                <div className="bg-[#f5f7fb] rounded-lg p-7 md:p-9 h-full">
                  <h3 className="text-lg font-semibold text-[#1b2653] mb-6">{tr.step3}</h3>
                  <div className="space-y-5">
                    <div>
                      <Label htmlFor="apt-name" className="text-[#1b2653] text-sm">{tr.name}</Label>
                      <Input id="apt-name" name="name" value={form.name} onChange={handleChange} placeholder={tr.namePh} className="mt-2 bg-white h-11" data-testid="appointment-input-name" />
                    </div>
                    <div>
                      <Label htmlFor="apt-email" className="text-[#1b2653] text-sm">{tr.email}</Label>
                      <Input id="apt-email" name="email" type="email" value={form.email} onChange={handleChange} placeholder={tr.emailPh} className="mt-2 bg-white h-11" data-testid="appointment-input-email" />
                    </div>
                    <div>
                      <Label htmlFor="apt-phone" className="text-[#1b2653] text-sm">{tr.phone}</Label>
                      <Input id="apt-phone" name="phone" value={form.phone} onChange={handleChange} placeholder="+224 ..." className="mt-2 bg-white h-11" data-testid="appointment-input-phone" />
                    </div>
                    <div>
                      <Label className="text-[#1b2653] text-sm">{tr.service}</Label>
                      <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                        <SelectTrigger className="mt-2 bg-white h-11" data-testid="appointment-select-service">
                          <SelectValue placeholder={tr.servicePh} />
                        </SelectTrigger>
                        <SelectContent>
                          {data.services.map((s) => (
                            <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>
                          ))}
                          <SelectItem value={tr.other}>{tr.other}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="apt-notes" className="text-[#1b2653] text-sm">{tr.notes}</Label>
                      <Textarea id="apt-notes" name="notes" value={form.notes} onChange={handleChange} placeholder={tr.notesPh} rows={4} className="mt-2 bg-white resize-none" data-testid="appointment-input-notes" />
                    </div>

                    {date && selectedSlot && (
                      <div className="bg-[#eef3fc] rounded-md px-4 py-3 text-sm text-[#1b2653]" data-testid="appointment-recap">
                        <strong className="font-semibold">{tr.recap}</strong>{" "}
                        {format(date, "EEEE d MMMM yyyy", { locale: dateLocale })} {tr.at} {selectedSlot}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full inline-flex items-center justify-center gap-2.5 rounded-full px-9 py-3.5 bg-[#1b2653] text-white text-[13px] font-semibold uppercase tracking-[0.18em] hover:bg-[#2f6fd8] transition-colors duration-300 disabled:opacity-60"
                      data-testid="appointment-submit-btn"
                    >
                      <Send size={15} />
                      {submitting ? tr.sending : tr.submit}
                    </button>
                  </div>
                </div>
              </Reveal>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Appointment;
